import { useEffect, useMemo, type FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useDynamicReducer } from '@/domains/core/applicationState/hooks/useDynamicReducer';
import { useDynamicSaga } from '@/domains/core/applicationState/hooks/useDynamicSaga';
import { isB2B, isB2C } from '@/domains/core/settings/utils';
import { RecommendationPlaylistSkeleton } from '@/domains/productDiscovery/Recommendation/components/Playlists/components/RecommendationPlaylistSkeleton/RecommendationPlaylistSkeleton';
import { FETCH_RECO_TOP_SALES_CATEGORIES } from '@/domains/productDiscovery/Recommendation/modules/CategoryRecommendation/CategoryRecommendation.actions';
import { recoCategoriesReducer } from '@/domains/productDiscovery/Recommendation/modules/CategoryRecommendation/CategoryRecommendation.reducer';
import { watchFetchRecoCategories } from '@/domains/productDiscovery/Recommendation/modules/CategoryRecommendation/CategoryRecommendation.saga';
import { CATEGORIES_RECOMMENDATION } from '@/domains/productDiscovery/Recommendation/modules/ProductRecommendation/constants';
import { ProductRecommendation } from '@/domains/productDiscovery/Recommendation/modules/ProductRecommendation/ProductRecommendation';
import {
  selectCategoryRecommendationsConfig,
  selectTopSalesCategoriesRecommendations,
} from '@/domains/productDiscovery/Recommendation/selectors/categoryRecommendation';
import { mapTopSellRecommendations } from '@/domains/productDiscovery/Recommendation/utils/mapTopSellRecommendation/mapTopSellRecommendation';
import {
  addToCartProductRecommendationsSelector,
  selectA2CProductRecommendationConfigs,
} from '@/domains/shoppingCart/AddToCart/selectors/recommendations';
import { selectAddToCartModalIsFetching } from '@/domains/shoppingCart/selectors/selectAddToCartModalIsFetching';

import { ETopSellsRecoEngineNames } from '../../constants';

import styles from '../CommonStyles.module.scss';

const topSellPlaceholder = {
  engineName: 'TopSalesCategory_model',
  modelIds: new Array(5),
};

const categoriesPlaceholder = {
  engineName: 'TopSalesCategory_model',
  modelIds: new Array(30),
};

export const CategoriesTopSalesContainerA2C: FC = () => {
  const dispatch = useDispatch();

  useDynamicReducer(
    `productDiscovery.${CATEGORIES_RECOMMENDATION}`,
    recoCategoriesReducer,
  );

  useDynamicSaga(
    `productDiscovery.${CATEGORIES_RECOMMENDATION}`,
    watchFetchRecoCategories,
  );

  useEffect(() => {
    dispatch({
      type: FETCH_RECO_TOP_SALES_CATEGORIES,
    });
  }, [dispatch]);

  const categoriesRecommendationsConfig = useSelector(
    selectCategoryRecommendationsConfig,
  );

  const categoriesRecommendations = useSelector(
    selectTopSalesCategoriesRecommendations,
  );

  const addToCartProductRecommendations = useSelector(
    addToCartProductRecommendationsSelector,
  );

  const topSellRecommendationConfig = useSelector(
    selectA2CProductRecommendationConfigs,
  );

  const isFetching = useSelector(selectAddToCartModalIsFetching);

  const playlistRecoEngine = ETopSellsRecoEngineNames;

  const topSellRecommendations = useMemo(() => {
    const playList = addToCartProductRecommendations
      ?.filter(
        (recommendation) =>
          !!recommendation.products.length &&
          Object.values(playlistRecoEngine).includes(
            recommendation.engineName as string,
          ),
      )
      .map((recommendation) => ({
        ...recommendation,
        title:
          recommendation.products[0].categoriesLast?.[0] ||
          recommendation.title,
      }));

    return playList;
  }, [addToCartProductRecommendations, playlistRecoEngine]);

  const mappedTopSellRecommendationConfig = mapTopSellRecommendations(
    topSellRecommendationConfig,
    playlistRecoEngine,
  );

  if (isFetching)
    return (
      <div className={styles.playlistsWrapper}>
        <RecommendationPlaylistSkeleton />
        <RecommendationPlaylistSkeleton />
        <RecommendationPlaylistSkeleton />
      </div>
    );

  if (
    mappedTopSellRecommendationConfig?.length &&
    topSellRecommendations?.length
  ) {
    return (
      <>
        {mappedTopSellRecommendationConfig
          .slice(0, 3)
          .filter((config) =>
            topSellRecommendations.find(
              (reco) => reco.engineName === config.engineName,
            ),
          )
          .map((config, i) => (
            <ProductRecommendation
              key={`top-sales-playlist-${config.engineName ?? config.name}`}
              className={styles.recommendation}
              sliderClassName={styles.slider}
              withProductCardsLight={isB2C()}
              playlistLocation="A2C_Popin"
              placeHolderSelector={() => topSellPlaceholder}
              recommendationSelector={() => topSellRecommendations[i]}
              addToCart
              isBrandsFilterEnabled={isB2B()}
            />
          ))}
      </>
    );
  }

  return (
    <>
      {categoriesRecommendationsConfig?.categoryIds
        ?.slice(0, 3)
        .map((categoryId, i) => (
          <ProductRecommendation
            key={categoryId}
            className={styles.recommendation}
            sliderClassName={styles.slider}
            withProductCardsLight={isB2C()}
            playlistLocation="A2C_Popin"
            placeHolderSelector={() => categoriesPlaceholder}
            recommendationSelector={() => categoriesRecommendations?.[i]?.[0]}
            addToCart
            isBrandsFilterEnabled={isB2B()}
          />
        ))}
    </>
  );
};
